import { supabase } from '../lib/supabase'
import {
  SuperappUser,
  SuperappUserBehavior,
  DashboardMetrics,
  SuperappUserFilters,
  SuperappBehaviorFilters,
  SuperappPromotion,
  SuperappPromotionFilters
} from '../types/superapp'

// =====================================================
// SERVIÇOS DO SUPERAPP DASHBOARD
// =====================================================

export const superappService = {
  // Obter todos os usuários com filtros opcionais
  async getAllUsers(filters: SuperappUserFilters = {}): Promise<SuperappUser[]> {
    try {
      let query = supabase
        .from('superapp_users')
        .select('*')

      if (filters.name) {
        query = query.ilike('name', `%${filters.name}%`)
      }
      if (filters.email) {
        query = query.ilike('email', `%${filters.email}%`)
      }
      if (filters.cpf) {
        query = query.ilike('cpf', `%${filters.cpf}%`)
      }
      if (filters.phone) {
        query = query.ilike('phone', `%${filters.phone}%`)
      }

      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Erro ao buscar usuários:', error)
      throw error
    }
  },

  // Obter usuário por ID
  async getUserById(userId: string): Promise<SuperappUser | null> {
    try {
      const { data, error } = await supabase
        .from('superapp_users')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Erro ao buscar usuário:', error)
      throw error
    }
  },

  // Mapa user_id -> { is_active, risk_churn } a partir do comportamento mais recente (para filtros na aba Usuários)
  async getUsersBehaviorStatusMap(): Promise<Record<string, { is_active: boolean; risk_churn: boolean }>> {
    try {
      const { data: behaviorsData, error } = await supabase
        .from('superapp_user_behavior')
        .select('user_id, behavior_data')
        .order('created_at', { ascending: false })

      if (error) throw error
      const map: Record<string, { is_active: boolean; risk_churn: boolean }> = {}
      for (const row of behaviorsData || []) {
        const uid = row.user_id
        if (!uid || map[uid] !== undefined) continue
        const comp = (row.behavior_data as any)?.[0]?.comportamento
        map[uid] = {
          is_active: comp?.is_active ?? false,
          risk_churn: comp?.risk_churn ?? false
        }
      }
      return map
    } catch (error) {
      console.error('Erro ao buscar status de comportamento dos usuários:', error)
      return {}
    }
  },

  // Obter contagem de comportamentos de um usuário
  async getUserBehaviorCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('superapp_user_behavior')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)

      if (error) throw error
      return count || 0
    } catch (error) {
      console.error('Erro ao contar comportamentos:', error)
      return 0
    }
  },

  // Obter todos os comportamentos com filtros opcionais e relacionamento com usuários
  async getUserBehaviors(filters: SuperappBehaviorFilters = {}): Promise<SuperappUserBehavior[]> {
    try {
      // Buscar comportamentos
      let query = supabase
        .from('superapp_user_behavior')
        .select('*')

      const { data: behaviorsData, error } = await query.order('created_at', { ascending: false })

      if (error) throw error

      // Buscar usuários relacionados
      const userIds = Array.from(new Set(
        (behaviorsData || [])
          .map(b => b.user_id)
          .filter((id): id is string => id !== null && id !== undefined)
      ))

      let usersMap = new Map<string, SuperappUser>()
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from('superapp_users')
          .select('*')
          .in('user_id', userIds)

        if (usersData) {
          usersData.forEach(user => {
            usersMap.set(user.user_id, user)
          })
        }
      }

      // Aplicar filtros de nome e email e anexar dados do usuário
      let filteredData = (behaviorsData || []).map((behavior: any) => ({
        ...behavior,
        user: behavior.user_id ? usersMap.get(behavior.user_id) || null : null
      }))

      if (filters.name || filters.email) {
        filteredData = filteredData.filter((behavior: any) => {
          // Primeiro tenta buscar do relacionamento com superapp_users
          if (behavior.user) {
            const nameMatch = !filters.name || (behavior.user.name?.toLowerCase().includes(filters.name!.toLowerCase()) ?? false)
            const emailMatch = !filters.email || (behavior.user.email?.toLowerCase().includes(filters.email!.toLowerCase()) ?? false)
            if (nameMatch && emailMatch) return true
          }

          // Se não encontrou no relacionamento, busca no JSON behavior_data
          if (behavior.behavior_data && behavior.behavior_data.length > 0) {
            const users = behavior.behavior_data
            const nameMatch = !filters.name || users.some((u: any) =>
              u.name?.toLowerCase().includes(filters.name!.toLowerCase())
            )
            const emailMatch = !filters.email || users.some((u: any) =>
              u.email?.toLowerCase().includes(filters.email!.toLowerCase())
            )

            return nameMatch && emailMatch
          }

          return false
        })
      }

      return filteredData
    } catch (error) {
      console.error('Erro ao buscar comportamentos:', error)
      throw error
    }
  },

  // Obter comportamento por tenant_id com relacionamento com usuário
  async getBehaviorByTenantId(tenantId: string): Promise<SuperappUserBehavior | null> {
    try {
      const { data: behavior, error } = await supabase
        .from('superapp_user_behavior')
        .select('*')
        .eq('tenant_id', tenantId)
        .single()

      if (error) throw error
      if (!behavior) return null

      // Buscar usuário relacionado
      let user: SuperappUser | null = null
      if (behavior.user_id) {
        const { data: userData } = await supabase
          .from('superapp_users')
          .select('*')
          .eq('user_id', behavior.user_id)
          .single()

        user = userData || null
      }

      return {
        ...behavior,
        user
      }
    } catch (error) {
      console.error('Erro ao buscar comportamento:', error)
      throw error
    }
  },

  // Obter métricas do dashboard
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      // Contagem total de usuários
      const { count: totalUsers, error: usersError } = await supabase
        .from('superapp_users')
        .select('*', { count: 'exact', head: true })

      if (usersError) throw usersError

      // Contagem total de comportamentos
      const { count: totalBehaviors, error: behaviorsError } = await supabase
        .from('superapp_user_behavior')
        .select('*', { count: 'exact', head: true })

      if (behaviorsError) throw behaviorsError

      // Buscar todos os usuários para calcular métricas de cadastro por data
      // Buscar todos os campos para garantir que não há limitação
      const { data: usersData, error: usersDataError } = await supabase
        .from('superapp_users')
        .select('created_at')
        .order('created_at', { ascending: false })

      if (usersDataError) {
        console.error('Erro ao buscar usuários:', usersDataError)
        throw usersDataError
      }

      // Buscar todos os comportamentos para calcular métricas
      const { data: behaviorsData, error: behaviorsDataError } = await supabase
        .from('superapp_user_behavior')
        .select('behavior_data, created_at')
        .order('created_at', { ascending: false })

      if (behaviorsDataError) throw behaviorsDataError

      // Calcular usuários ativos e em risco
      let activeUsers = 0
      let atRiskUsers = 0
      let totalEngagementScore = 0
      let engagementScoreCount = 0

      const usersByDateMap = new Map<string, number>()
      const usersByStatusMap = new Map<string, number>()
      const favoriteModulesMap = new Map<string, number>()
      const preferredTimeSlotsMap = new Map<string, number>()
      const engagementTrendMap = new Map<string, { sum: number; count: number }>()

      // Agrupar usuários cadastrados por data (últimos 3 meses)
      // Usar timezone de São Paulo para consistência com a exibição na tabela
      const threeMonthsAgo = new Date()
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
      
      // Função auxiliar para obter data no formato YYYY-MM-DD usando timezone de São Paulo
      const getDateInSPTimezone = (date: Date): string => {
        const dateSP = new Date(date.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }))
        const year = dateSP.getFullYear()
        const month = String(dateSP.getMonth() + 1).padStart(2, '0')
        const day = String(dateSP.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      
      // Obter data de corte no timezone de São Paulo
      const threeMonthsAgoDate = getDateInSPTimezone(threeMonthsAgo)
      
      if (usersData && usersData.length > 0) {
        usersData.forEach((user) => {
          if (user.created_at) {
            const userCreatedDate = new Date(user.created_at)
            // Obter data no formato YYYY-MM-DD usando timezone de São Paulo
            const userDateSP = getDateInSPTimezone(userCreatedDate)
            
            // Comparar strings de data (YYYY-MM-DD) para filtrar últimos 3 meses
            if (userDateSP >= threeMonthsAgoDate) {
              const currentCount = usersByDateMap.get(userDateSP) || 0
              usersByDateMap.set(userDateSP, currentCount + 1)
            }
          }
        })
      }

      behaviorsData?.forEach((behavior) => {
        if (behavior.behavior_data && behavior.behavior_data.length > 0) {
          behavior.behavior_data.forEach((user: any) => {
            if (user.comportamento?.is_active) {
              activeUsers++
            }
            if (user.comportamento?.risk_churn) {
              atRiskUsers++
            }

            // Calcular média de score de engajamento
            if (user.comportamento?.engagement_score !== undefined && user.comportamento?.engagement_score !== null) {
              totalEngagementScore += user.comportamento.engagement_score
              engagementScoreCount++
            }

            // Contar por status
            const status = user.comportamento?.is_active ? 'Ativo' : 'Inativo'
            usersByStatusMap.set(status, (usersByStatusMap.get(status) || 0) + 1)

            // Contar módulos favoritos
            if (user.comportamento?.favorite_module) {
              const module = user.comportamento.favorite_module
              favoriteModulesMap.set(module, (favoriteModulesMap.get(module) || 0) + 1)
            }

            // Contar horários preferidos
            if (user.comportamento?.preferred_time_slot) {
              const timeSlot = user.comportamento.preferred_time_slot
              preferredTimeSlotsMap.set(timeSlot, (preferredTimeSlotsMap.get(timeSlot) || 0) + 1)
            }

            // Calcular tendência de engajamento por usuário
            if (user.comportamento?.engagement_score !== undefined && user.comportamento?.engagement_score !== null && behavior.created_at) {
              const date = new Date(behavior.created_at).toISOString().split('T')[0]
              const current = engagementTrendMap.get(date) || { sum: 0, count: 0 }
              engagementTrendMap.set(date, {
                sum: current.sum + user.comportamento.engagement_score,
                count: current.count + 1
              })
            }
          })
        }
      })

      // Calcular média de score de engajamento
      const avgEngagementScore = engagementScoreCount > 0 ? totalEngagementScore / engagementScoreCount : 0

      // Converter mapas para arrays
      const behaviorsByDate = Array.from(usersByDateMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date))
      

      const usersByStatus = Array.from(usersByStatusMap.entries()).map(([status, count]) => ({
        status,
        count
      }))

      const favoriteModules = Array.from(favoriteModulesMap.entries())
        .map(([module, count]) => ({
          module,
          count
        }))
        .sort((a, b) => b.count - a.count) // Ordenar por quantidade (maior primeiro)

      const preferredTimeSlots = Array.from(preferredTimeSlotsMap.entries())
        .map(([timeSlot, count]) => ({
          timeSlot,
          count
        }))
        .sort((a, b) => b.count - a.count) // Ordenar por quantidade (maior primeiro)

      const engagementTrend = Array.from(engagementTrendMap.entries())
        .map(([date, { sum, count }]) => ({
          date,
          score: count > 0 ? sum / count : 0
        }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-30) // Últimos 30 dias

      return {
        totalUsers: totalUsers || 0,
        totalBehaviors: totalBehaviors || 0,
        activeUsers,
        atRiskUsers,
        avgEngagementScore,
        behaviorsByDate,
        usersByStatus,
        favoriteModules,
        preferredTimeSlots,
        engagementTrend
      }
    } catch (error) {
      console.error('Erro ao buscar métricas:', error)
      throw error
    }
  },

  // Obter todas as promoções com filtros opcionais
  async getPromotions(filters: SuperappPromotionFilters = {}): Promise<SuperappPromotion[]> {
    try {
      let query = supabase
        .from('superapp_promotions')
        .select('*')

      if (filters.title) {
        query = query.ilike('title', `%${filters.title}%`)
      }
      if (filters.establishment_name) {
        query = query.ilike('establishment_name', `%${filters.establishment_name}%`)
      }
      if (filters.type) {
        query = query.ilike('type', `%${filters.type}%`)
      }
      if (filters.start_date) {
        query = query.gte('start_date', filters.start_date)
      }
      if (filters.end_date) {
        query = query.lte('end_date', filters.end_date)
      }

      const { data, error } = await query.order('start_date', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Erro ao buscar promoções:', error)
      throw error
    }
  }
}
